import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { AnimatePresence, motion } from "framer-motion";

import {
  FiAlertCircle,
  FiArrowRight,
  FiBookOpen,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiDollarSign,
  FiGlobe,
  FiInfo,
  FiLoader,
  FiMail,
  FiRefreshCw,
  FiSend,
  FiShield,
  FiUsers,
  FiX,
} from "react-icons/fi";

/* =========================================================
   HELPERS
========================================================= */

const hasValue = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== "";

const displayValue = (value) =>
  hasValue(value) ? String(value) : "Not provided";

const formatLabel = (value) =>
  !hasValue(value)
    ? "Not set"
    : String(value)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) =>
          letter.toUpperCase()
        );

const formatDate = (value, includeTime = false) => {
  if (!hasValue(value)) return "Not provided";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not provided";

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        }
      : {}),
  }).format(date);
};

const formatRevenue = (value, currency) => {
  if (!hasValue(value)) return "Not provided";

  const amount = Number(value);
  if (!Number.isFinite(amount)) return displayValue(value);

  if (hasValue(currency)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: String(currency),
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      // Fall back to numeric formatting.
    }
  }

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getInitials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "CL";

const getStatusStyles = (status) => {
  const normalized = String(status || "").toLowerCase();

  switch (normalized) {
    case "active":
      return {
        label: "Active",
        className: "bg-emerald-50 text-emerald-700 ring-emerald-100",
        dot: "bg-emerald-500",
      };

    case "pending":
      return {
        label: "Pending",
        className: "bg-amber-50 text-amber-700 ring-amber-100",
        dot: "bg-amber-500",
      };

    case "inactive":
    case "archived":
      return {
        label: formatLabel(normalized),
        className: "bg-slate-100 text-slate-600 ring-slate-200",
        dot: "bg-slate-400",
      };

    default:
      return {
        label: formatLabel(status),
        className: "bg-slate-50 text-slate-500 ring-slate-200",
        dot: "bg-slate-400",
      };
  }
};

const isValidEmail = (email) =>
  typeof email === "string" &&
  email.trim().length >= 3 &&
  email.trim().length <= 254 &&
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

async function readFunctionError(error) {
  let message = error?.message || "The request failed.";
  const status = error?.context?.status;

  try {
    if (typeof error?.context?.json === "function") {
      const response = await error.context.json();
      message = response?.error || response?.message || message;
    }
  } catch {
    // Preserve the original error.
  }

  return {
    message,
    status,
  };
}

/* =========================================================
   DETAIL COMPONENTS
========================================================= */

function DetailField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3.5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
        <Icon className="text-[15px]" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-slate-500">
          {label}
        </p>

        <p
          className={`mt-1 break-words text-[13px] font-medium leading-relaxed ${
            !hasValue(value) || value === "Not provided"
              ? "text-slate-400"
              : "text-slate-800"
          }`}
        >
          {displayValue(value)}
        </p>
      </div>
    </div>
  );
}

function DrawerSection({ title, description, children }) {
  return (
    <section>
      <div className="mb-3">
        <h3 className="text-[13px] font-semibold text-slate-900">
          {title}
        </h3>

        {description && (
          <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
            {description}
          </p>
        )}
      </div>

      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white px-4">
        {children}
      </div>
    </section>
  );
}

/* =========================================================
   PORTAL STATUS DISPLAY

   Data comes from verified backend responses.
   Business status is never used as portal status.
========================================================= */

const portalDisplay = {
  active: {
    title: "Portal Access Active",
    badge: "Active",
    description:
      "The client owner has access to this organization's Client Portal.",
    icon: FiCheckCircle,
    color: "text-emerald-700",
    background: "bg-emerald-50",
    border: "border-emerald-200",
  },

  pending: {
    title: "Invitation Pending",
    badge: "Pending",
    description:
      "An invitation has been created and is awaiting acceptance.",
    icon: FiClock,
    color: "text-amber-700",
    background: "bg-amber-50",
    border: "border-amber-200",
  },

  expired: {
    title: "Invitation Expired",
    badge: "Expired",
    description:
      "The previous invitation has expired. A new invitation can be created.",
    icon: FiAlertCircle,
    color: "text-amber-700",
    background: "bg-amber-50",
    border: "border-amber-200",
  },

  revoked: {
    title: "Invitation Revoked",
    badge: "Revoked",
    description:
      "The previous invitation was revoked. A new invitation can be created.",
    icon: FiAlertCircle,
    color: "text-slate-600",
    background: "bg-slate-50",
    border: "border-slate-200",
  },

  not_invited: {
    title: "Client Not Invited",
    badge: "Not Invited",
    description:
      "This client does not currently have a client owner invitation or active owner membership.",
    icon: FiMail,
    color: "text-slate-600",
    background: "bg-slate-50",
    border: "border-slate-200",
  },

  needs_review: {
    title: "Access Needs Review",
    badge: "Needs Review",
    description:
      "An invitation was accepted, but no client owner membership was found. Check the onboarding records before sending another invitation.",
    icon: FiAlertCircle,
    color: "text-amber-700",
    background: "bg-amber-50",
    border: "border-amber-200",
  },
};

function PortalStatusCard({
  access,
  onInvite,
  inviting,
}) {
  const config =
    portalDisplay[access?.status] ||
    portalDisplay.needs_review;

  const Icon = config.icon;

  const canCreateInvitation = [
    "not_invited",
    "expired",
    "revoked",
  ].includes(access?.status);

  return (
    <div className="space-y-4">

      <div
        className={`rounded-xl border p-3.5 ${config.border} ${config.background}`}
      >
        <div className="flex items-start gap-3">

          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ${config.color}`}>
            <Icon className="text-lg" />
          </div>

          <div className="min-w-0 flex-1">

            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[12px] font-semibold text-slate-900">
                {config.title}
              </p>

              <span className={`rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold ${config.color}`}>
                {config.badge}
              </span>
            </div>

            <p className="mt-2 text-[11px] leading-relaxed text-slate-600">
              {config.description}
            </p>

          </div>
        </div>
      </div>

      {/* VERIFIED CLIENT OWNER */}

      {access.status === "active" && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3.5">
          {access.owner_email && (
            <div>
              <p className="text-[10px] font-medium text-slate-400">
                Client Owner
              </p>

              <p className="mt-1 break-all text-[12px] font-semibold text-slate-800">
                {access.owner_email}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 text-[11px] text-emerald-700">
            <FiShield />
            Organization membership verified
          </div>
        </div>
      )}

      {/* EXISTING INVITATION DETAILS */}

      {access.status !== "active" &&
        access.invitation && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3.5">

            <div>
              <p className="text-[10px] font-medium text-slate-400">
                Invited Email
              </p>

              <p className="mt-1 break-all text-[12px] font-semibold text-slate-800">
                {access.invitation.invited_email}
              </p>
            </div>

            <div>
              <p className="text-[10px] font-medium text-slate-400">
                Invitation Expiration
              </p>

              <p className="mt-1 text-[12px] text-slate-700">
                {formatDate(
                  access.invitation.expires_at,
                  true
                )}
              </p>
            </div>

            {access.invitation.accepted_at && (
              <div>
                <p className="text-[10px] font-medium text-slate-400">
                  Accepted On
                </p>

                <p className="mt-1 text-[12px] text-slate-700">
                  {formatDate(
                    access.invitation.accepted_at,
                    true
                  )}
                </p>
              </div>
            )}

          </div>
        )}

      {canCreateInvitation && (
        <button
          type="button"
          onClick={onInvite}
          disabled={inviting}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2.5 text-[12px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-50"
        >
          <FiMail />
          {access.status === "not_invited"
            ? "Invite Client Owner"
            : "Create New Invitation"}
        </button>
      )}

      {access.status === "pending" && (
        <p className="text-[10px] leading-relaxed text-slate-500">
          The existing invitation must be accepted,
          expire, or be revoked before another can be created.
        </p>
      )}

    </div>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function ClientDetailsDrawer({
  client,
  onClose,
  onOpenBooks,
  supabase,
  clientOrganizationId,
  canInvite = false,
  onInvitationCreated,
}) {
  const closeButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const closeRef = useRef(onClose);

  const requestSequenceRef = useRef(0);
  const inviteLockRef = useRef(false);

  const [isOpeningBooks, setIsOpeningBooks] =
    useState(false);

  const [showInviteForm, setShowInviteForm] =
    useState(false);

  const [inviteEmail, setInviteEmail] =
    useState("");

  const [isInviting, setIsInviting] =
    useState(false);

  const [inviteError, setInviteError] =
    useState("");

  const [inviteSuccess, setInviteSuccess] =
    useState(null);

  const [copied, setCopied] =
    useState(false);

  const [portalAccess, setPortalAccess] =
    useState(null);

  const [portalLoading, setPortalLoading] =
    useState(false);

  const [portalError, setPortalError] =
    useState("");

  const isOpen = Boolean(client?.id);

  const organizationId =
    clientOrganizationId ??
    client?.client_organization_id ??
    client?.clientOrganizationId ??
    client?.organization_id ??
    client?.organizationId ??
    null;

  /* =======================================================
     PORTAL STATUS LOOKUP

     All statuses are provided by the Edge Function.
     Do not derive them from client.status.
  ======================================================= */

  const loadPortalAccess = useCallback(async () => {
    if (
      !isOpen ||
      !canInvite ||
      !organizationId ||
      !supabase?.functions?.invoke
    ) {
      return;
    }

    const sequence = ++requestSequenceRef.current;
    const expectedId = organizationId;

    setPortalLoading(true);
    setPortalError("");

    try {
      const { data, error } =
        await supabase.functions.invoke(
          "client-portal-access-status",
          {
            body: {
              client_organization_id: expectedId,
            },
          }
        );

      if (error) {
        const details = await readFunctionError(error);
        throw new Error(details.message);
      }

      if (
        !data?.success ||
        data.client_organization_id !== expectedId
      ) {
        throw new Error(
          "Unable to verify Client Portal access."
        );
      }

      if (sequence !== requestSequenceRef.current) {
        return;
      }

      setPortalAccess(data);
    } catch (error) {
      if (sequence !== requestSequenceRef.current) {
        return;
      }

      setPortalAccess(null);
      setPortalError(
        error?.message ||
          "Unable to load portal access status."
      );
    } finally {
      if (sequence === requestSequenceRef.current) {
        setPortalLoading(false);
      }
    }
  }, [
    isOpen,
    canInvite,
    organizationId,
    supabase,
  ]);

  useEffect(() => {
    requestSequenceRef.current += 1;

    setPortalAccess(null);
    setPortalError("");
    setPortalLoading(false);

    setShowInviteForm(false);
    setInviteEmail("");
    setInviteError("");
    setInviteSuccess(null);
    setCopied(false);

    // The current request is invalidated when
    // a different client is selected.
    loadPortalAccess();

    return () => {
      requestSequenceRef.current += 1;
    };
  }, [
    organizationId,
    client?.id,
    isOpen,
    loadPortalAccess,
  ]);

  /* =======================================================
     FOCUS + ESCAPE + TAB
  ======================================================= */

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;

    const frame = requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current?.();
        return;
      }

      if (event.key !== "Tab") return;

      const drawer = drawerRef.current;
      if (!drawer) return;

      const focusable = Array.from(
        drawer.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (element) => element.getClientRects().length > 0
      );

      if (!focusable.length) {
        event.preventDefault();
        drawer.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

      const previous = previousFocusRef.current;

      if (
        previous?.isConnected &&
        typeof previous.focus === "function"
      ) {
        previous.focus();
      }
    };
  }, [isOpen]);

  /* =======================================================
     BODY SCROLL LOCK FOR DRAWER
  ======================================================= */

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isOpen]);

  /* =======================================================
     OPEN CLIENT BOOKS
  ======================================================= */

  const handleOpenBooks = async () => {
    if (
      !client?.id ||
      isOpeningBooks ||
      typeof onOpenBooks !== "function"
    ) {
      return;
    }

    setIsOpeningBooks(true);

    try {
      await onOpenBooks();
    } catch (error) {
      console.error("Unable to open client books:", error);
    } finally {
      setIsOpeningBooks(false);
    }
  };

  /* =======================================================
     SEND INVITATION

     Never mark access active based only on sending.
     Reload verified status afterward.
  ======================================================= */

  const handleInvite = async (event) => {
    event.preventDefault();

    if (
      !canInvite ||
      !organizationId ||
      !portalAccess ||
      !["not_invited", "expired", "revoked"].includes(
        portalAccess.status
      )
    ) {
      setInviteError(
        "This client is not currently eligible for a new invitation."
      );
      return;
    }

    if (inviteLockRef.current || isInviting) {
      return;
    }

    const email = inviteEmail.trim().toLowerCase();

    if (!isValidEmail(email)) {
      setInviteError("Enter a valid email address.");
      return;
    }

    if (!supabase?.functions?.invoke) {
      setInviteError("Invitation service unavailable.");
      return;
    }

    const expectedId = organizationId;
    const sequence = requestSequenceRef.current;

    inviteLockRef.current = true;
    setIsInviting(true);
    setInviteError("");

    try {
      const { data, error } =
        await supabase.functions.invoke(
          "invite-client-owner",
          {
            body: {
              client_organization_id: expectedId,
              invited_email: email,
            },
          }
        );

      if (error) {
        const details = await readFunctionError(error);

        if (sequence === requestSequenceRef.current) {
          // The backend may have a newer invitation.
          // Refresh before allowing another attempt.
          await loadPortalAccess();
        }

        throw new Error(
          details.status
            ? `${details.message} (HTTP ${details.status})`
            : details.message
        );
      }

      if (!data?.success || !data?.invitation_id) {
        throw new Error(
          data?.error || "Invitation could not be confirmed."
        );
      }

      if (sequence !== requestSequenceRef.current) {
        return;
      }

      setInviteSuccess({
        id: data.invitation_id,
        email: data.invited_email || email,
        expiresAt: data.expires_at,
        url: data.invitation_url || "",
        emailSent: data.email_sent === true,
      });

      setCopied(false);

      onInvitationCreated?.({
        invitation_id: data.invitation_id,
        client_organization_id: expectedId,
        invited_email: data.invited_email || email,
        expires_at: data.expires_at,
        email_sent: data.email_sent === true,
        email_status: data.email_status,
      });

      // Status will become "pending", not "active".
      await loadPortalAccess();
    } catch (error) {
      setInviteError(
        error?.message || "Unable to send invitation."
      );
    } finally {
      inviteLockRef.current = false;
      setIsInviting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteSuccess?.url) return;

    try {
      await navigator.clipboard.writeText(
        inviteSuccess.url
      );
      setCopied(true);
      setInviteError("");
    } catch {
      setInviteError(
        "Unable to copy the invitation link."
      );
    }
  };

  /* =======================================================
     CLIENT DETAILS
  ======================================================= */

  const status = getStatusStyles(client?.status);

  const businessType =
    client?.businessType ??
    client?.business_type ??
    null;

  const fiscalYear =
    client?.fiscalYear ??
    client?.fiscal_year ??
    null;

  const employeeCount =
    client?.employeeCount ??
    client?.employee_count ??
    null;

  const monthlyRevenue =
    client?.monthlyRevenue ??
    client?.monthly_revenue ??
    null;

  const bookkeepingManager =
    client?.bookkeepingManager ??
    client?.bookkeeping_manager ??
    null;

  const createdAt =
    client?.createdAt ??
    client?.created_at ??
    null;

  const initialEmail =
    client?.business_email ??
    client?.businessEmail ??
    "";

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex justify-end"
          role="presentation"
        >
          {/* BACKDROP */}

          <motion.button
            type="button"
            aria-label="Close client details"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 h-full w-full cursor-default bg-slate-950/35"
          />

          {/* DRAWER */}

          <motion.aside
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Client details for ${client.name || "client"}`}
            tabIndex={-1}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "tween",
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative z-10 flex h-full w-full max-w-[460px] flex-col overflow-hidden bg-white shadow-2xl"
          >
            {/* HEADER */}

            <div className="shrink-0 border-b border-slate-100 px-5 py-5 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                    Client Details
                  </p>

                  <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">
                    Business Information
                  </h2>
                </div>

                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  aria-label="Close client details"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <FiX className="text-lg" />
                </button>
              </div>
            </div>

            {/* SCROLLABLE CONTENT */}

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-6">

              {/* CLIENT IDENTITY */}

              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-sm font-bold text-emerald-700">
                  {getInitials(client.name)}
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="break-words text-base font-semibold text-slate-900">
                    {client.name || "Unnamed client"}
                  </h3>

                  {hasValue(client.legal_name) &&
                    client.legal_name !== client.name && (
                      <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        {client.legal_name}
                      </p>
                    )}

                  <span
                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${status.className}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${status.dot}`}
                    />
                    {status.label}
                  </span>
                </div>
              </div>

              {/* CLIENT INFORMATION */}

              <div className="mt-5 flex items-start gap-2 rounded-xl bg-slate-50 px-3.5 py-3">
                <FiInfo className="mt-0.5 shrink-0 text-slate-400" />

                <p className="text-[11px] leading-relaxed text-slate-500">
                  These are the business details available
                  for this client in your authorized workspace.
                </p>
              </div>

              {/* CLIENT PORTAL ACCESS */}

              {canInvite && (
                <div className="mt-7 rounded-xl border border-slate-200 bg-slate-50/60 p-4">

                  <div className="flex items-start gap-3">

                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700">
                      <FiShield />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-[13px] font-semibold text-slate-900">
                        Client Portal Access
                      </h3>

                      <p className="mt-1 text-[11px] leading-relaxed text-slate-600">
                        View and manage the client owner's
                        organization access.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={loadPortalAccess}
                      disabled={portalLoading || isInviting}
                      aria-label="Refresh portal access"
                      title="Refresh portal access"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <FiRefreshCw
                        className={
                          portalLoading
                            ? "animate-spin"
                            : ""
                        }
                      />
                    </button>
                  </div>

                  <div className="mt-4">

                    {portalLoading && !portalAccess ? (
                      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-4 text-[12px] text-slate-500">
                        <FiLoader className="animate-spin" />
                        Checking client portal access...
                      </div>
                    ) : portalError && !portalAccess ? (
                      <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 p-3"
                      >
                        <p className="text-[12px] text-red-700">
                          {portalError}
                        </p>

                        <button
                          type="button"
                          onClick={loadPortalAccess}
                          className="mt-3 text-[11px] font-semibold text-red-800 underline"
                        >
                          Try Again
                        </button>
                      </div>
                    ) : portalAccess && !showInviteForm ? (
                      <PortalStatusCard
                        access={portalAccess}
                        inviting={isInviting}
                        onInvite={() => {
                          setInviteEmail(
                            portalAccess.invitation?.invited_email ||
                            (hasValue(initialEmail)
                              ? String(initialEmail).trim()
                              : "")
                          );
                          setInviteError("");
                          setInviteSuccess(null);
                          setCopied(false);
                          setShowInviteForm(true);
                        }}
                      />
                    ) : null}

                    {/* INVITATION FORM */}

                    {showInviteForm && (
                      <div className="rounded-xl border border-slate-200 bg-white p-3.5">

                        {inviteSuccess ? (
                          <div role="status" className="space-y-3">

                            <div className="flex items-center gap-2 text-emerald-700">
                              <FiCheckCircle />
                              <p className="text-[12px] font-semibold">
                                Invitation Created
                              </p>
                            </div>

                            <p className="break-all text-[12px] text-slate-700">
                              {inviteSuccess.email}
                            </p>

                            {inviteSuccess.emailSent ? (
                              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                <p className="text-[12px] font-semibold text-emerald-800">
                                  Invitation Email Sent
                                </p>

                                <p className="mt-1 text-[11px] leading-relaxed text-emerald-700">
                                  The email server accepted
                                  the message. The recipient
                                  can check their inbox.
                                </p>
                              </div>
                            ) : (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <p className="text-[12px] font-semibold text-amber-900">
                                  Email Not Sent
                                </p>

                                <p className="mt-1 text-[11px] leading-relaxed text-amber-800">
                                  The invitation was created,
                                  but email sending failed.
                                  Share the secure link with
                                  the intended recipient.
                                </p>
                              </div>
                            )}

                            <p className="text-[11px] text-slate-500">
                              Expires:{" "}
                              {formatDate(
                                inviteSuccess.expiresAt,
                                true
                              )}
                            </p>

                            {inviteSuccess.url && (
                              <button
                                type="button"
                                onClick={handleCopyLink}
                                className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2.5 text-[12px] font-semibold text-white hover:bg-slate-800"
                              >
                                <FiCopy />
                                {copied
                                  ? "Link Copied"
                                  : "Copy Invitation Link"}
                              </button>
                            )}

                            {inviteError && (
                              <p
                                role="alert"
                                className="text-[11px] text-red-700"
                              >
                                {inviteError}
                              </p>
                            )}

                            <button
                              type="button"
                              onClick={() => {
                                setShowInviteForm(false);
                                setInviteSuccess(null);
                                setInviteEmail("");
                                setInviteError("");
                                loadPortalAccess();
                              }}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Done
                            </button>
                          </div>
                        ) : (
                          <form
                            onSubmit={handleInvite}
                            className="space-y-4"
                          >
                            <div>
                              <label
                                htmlFor="client-invitation-email"
                                className="block text-[11px] font-semibold text-slate-700"
                              >
                                Client Owner's Email
                              </label>

                              <input
                                id="client-invitation-email"
                                type="email"
                                autoComplete="email"
                                maxLength={254}
                                required
                                disabled={isInviting}
                                value={inviteEmail}
                                onChange={(event) => {
                                  setInviteEmail(event.target.value);
                                  setInviteError("");
                                }}
                                placeholder="owner@clientcompany.com"
                                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[13px] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:opacity-60"
                              />
                            </div>

                            <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3">
                              <FiShield className="mt-0.5 shrink-0 text-emerald-700" />

                              <p className="text-[11px] leading-relaxed text-slate-600">
                                A secure invitation will be
                                emailed to the client owner.
                                Access is restricted to this
                                organization and invitations
                                expire after 72 hours.
                              </p>
                            </div>

                            {inviteError && (
                              <p
                                role="alert"
                                className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-[11px] text-red-700"
                              >
                                <FiAlertCircle className="mt-0.5 shrink-0" />
                                {inviteError}
                              </p>
                            )}

                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={isInviting}
                                onClick={() => {
                                  setShowInviteForm(false);
                                  setInviteError("");
                                }}
                                className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              >
                                Cancel
                              </button>

                              <button
                                type="submit"
                                disabled={
                                  isInviting ||
                                  !isValidEmail(inviteEmail)
                                }
                                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {isInviting ? (
                                  <>
                                    <FiLoader className="animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <FiSend />
                                    Send Invitation
                                  </>
                                )}
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              )}

              {/* BUSINESS PROFILE */}

              <div className="mt-7">
                <DrawerSection
                  title="Business Profile"
                  description="General information about this client."
                >
                  <DetailField
                    icon={FiBriefcase}
                    label="Business Type"
                    value={businessType}
                  />

                  <DetailField
                    icon={FiBriefcase}
                    label="Industry"
                    value={client.industry}
                  />

                  <DetailField
                    icon={FiGlobe}
                    label="Country"
                    value={client.country}
                  />

                  <DetailField
                    icon={FiDollarSign}
                    label="Currency"
                    value={client.currency}
                  />
                </DrawerSection>
              </div>

              {/* ACCOUNTING DETAILS */}

              <div className="mt-7">
                <DrawerSection
                  title="Accounting Details"
                  description="Information relevant to the client's bookkeeping."
                >
                  <DetailField
                    icon={FiCalendar}
                    label="Fiscal Year"
                    value={fiscalYear}
                  />

                  <DetailField
                    icon={FiUsers}
                    label="Number of Employees"
                    value={employeeCount}
                  />

                  <DetailField
                    icon={FiDollarSign}
                    label="Monthly Revenue"
                    value={
                      hasValue(monthlyRevenue)
                        ? formatRevenue(
                            monthlyRevenue,
                            client.currency
                          )
                        : null
                    }
                  />

                  <DetailField
                    icon={FiUsers}
                    label="Bookkeeping Manager"
                    value={bookkeepingManager}
                  />
                </DrawerSection>
              </div>

              {/* OTHER INFORMATION */}

              <div className="mt-7">
                <DrawerSection title="Other Information">
                  <DetailField
                    icon={FiClock}
                    label="Date Created"
                    value={formatDate(createdAt)}
                  />
                </DrawerSection>
              </div>

              <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
                Missing information is shown as
                "Not provided." Financial records are
                managed in the client's accounting workspace.
              </p>
            </div>

            {/* FOOTER */}

            <div className="shrink-0 border-t border-slate-100 bg-white px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={handleOpenBooks}
                disabled={
                  isOpeningBooks ||
                  typeof onOpenBooks !== "function"
                }
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {isOpeningBooks ? (
                  <>
                    <FiClock className="animate-pulse" />
                    Opening Books...
                  </>
                ) : (
                  <>
                    <FiBookOpen className="text-base" />
                    Open Client Books
                    <FiArrowRight className="ml-1" />
                  </>
                )}
              </button>

              <p className="mt-2.5 text-center text-[11px] text-slate-400">
                Opens this client's accounting dashboard
              </p>
            </div>

          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}